/**
 * Rax GitHub Warning Banner
 *
 * Displays a dismissible warning banner when GitHub is not connected,
 * prompting users to connect for persistent skill/config storage.
 * Returns after 24 hours if dismissed.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

export type GitHubConnectionState =
  | "not_connected"
  | "connected"
  | "not_synced"
  | "token_expired";

export interface GitHubConnectionStatus {
  state: GitHubConnectionState;
  repoName?: string;
  lastSyncAt?: string;
  tokenExpiresAt?: string;
}

const DISMISS_STORAGE_KEY = "rax-github-banner-dismissed-at";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

@customElement("rax-github-warning-banner")
export class RaxGitHubWarningBanner extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }

      .banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        margin-bottom: var(--space-3);
        font-size: var(--font-sm);
        line-height: 1.4;
        animation: slideIn 0.2s ease-out;
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(-8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .banner.warning {
        border: 1px solid rgba(245, 158, 11, 0.5);
        background: rgba(245, 158, 11, 0.12);
        color: #fbbf24;
      }

      .banner.error {
        border: 1px solid rgba(239, 68, 68, 0.55);
        background: rgba(239, 68, 68, 0.14);
        color: #fca5a5;
      }

      .banner.info {
        border: 1px solid rgba(59, 130, 246, 0.5);
        background: rgba(59, 130, 246, 0.12);
        color: #93c5fd;
      }

      .banner-content {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex: 1;
      }

      .banner-icon {
        font-size: var(--font-lg);
        flex-shrink: 0;
      }

      .banner-text {
        flex: 1;
      }

      .banner-text strong {
        font-weight: 600;
      }

      .banner-actions {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-shrink: 0;
      }

      .banner-btn {
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        font-size: var(--font-xs);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
        text-decoration: none;
      }

      .banner-btn.primary {
        background: var(--rax-yellow);
        border: none;
        color: var(--rax-black);
      }

      .banner-btn.primary:hover {
        opacity: 0.9;
      }

      .banner-btn.secondary {
        background: transparent;
        border: 1px solid currentColor;
        color: inherit;
        opacity: 0.8;
      }

      .banner-btn.secondary:hover {
        opacity: 1;
      }

      .banner-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .dismiss-btn {
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        padding: var(--space-1);
        opacity: 0.6;
        font-size: var(--font-lg);
        line-height: 1;
      }

      .dismiss-btn:hover {
        opacity: 1;
      }

      .github-icon {
        width: 16px;
        height: 16px;
        vertical-align: middle;
        margin-right: var(--space-1);
      }

      @media (max-width: 640px) {
        .banner {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-2);
        }

        .banner-actions {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `,
  ];

  @property({ type: Object })
  status: GitHubConnectionStatus | null = null;

  @property({ type: Boolean })
  dismissible = true;

  @state()
  private dismissed = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.checkDismissedState();
  }

  private checkDismissedState(): void {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      // Banner returns after 24 hours
      if (now - dismissTime < DISMISS_DURATION_MS) {
        this.dismissed = true;
      } else {
        // Clear expired dismissal
        localStorage.removeItem(DISMISS_STORAGE_KEY);
      }
    }
  }

  private get shouldShow(): boolean {
    if (this.dismissed) return false;
    if (!this.status) return false;
    return this.status.state !== "connected";
  }

  private get bannerClass(): string {
    if (!this.status) return "warning";
    switch (this.status.state) {
      case "token_expired":
        return "error";
      case "not_synced":
        return "info";
      case "not_connected":
      default:
        return "warning";
    }
  }

  private get icon(): string {
    if (!this.status) return "";
    switch (this.status.state) {
      case "token_expired":
        return "";
      case "not_synced":
        return "";
      case "not_connected":
      default:
        return "";
    }
  }

  private get message(): string {
    if (!this.status) return "";
    switch (this.status.state) {
      case "token_expired":
        return "GitHub token expired. Reconnect to continue syncing your skills and config.";
      case "not_synced":
        return `Connected to ${this.status.repoName || "GitHub"}, but not synced. Your latest changes may not be saved.`;
      case "not_connected":
      default:
        return "GitHub not connected. Your custom tools aren't saved permanently.";
    }
  }

  private get actionText(): string {
    if (!this.status) return "Connect GitHub";
    switch (this.status.state) {
      case "token_expired":
        return "Reconnect";
      case "not_synced":
        return "Sync Now";
      case "not_connected":
      default:
        return "Connect GitHub";
    }
  }

  override render() {
    if (!this.shouldShow) {
      return nothing;
    }

    return html`
      <div class="banner ${this.bannerClass}" role="alert">
        <div class="banner-content">
          <span class="banner-icon">${this.icon}</span>
          <div class="banner-text">
            <strong>${this.message}</strong>
          </div>
        </div>
        <div class="banner-actions">
          <button
            class="banner-btn primary"
            @click=${this.handleConnect}
          >
            ${this.renderGitHubIcon()}
            ${this.actionText}
          </button>
          ${this.dismissible
            ? html`
                <button
                  class="dismiss-btn"
                  @click=${this.handleDismiss}
                  aria-label="Dismiss alert"
                >
                  x
                </button>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderGitHubIcon() {
    return html`
      <svg class="github-icon" viewBox="0 0 16 16" fill="currentColor">
        <path fill-rule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
      </svg>
    `;
  }

  private handleDismiss(): void {
    this.dismissed = true;
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    this.dispatchEvent(new CustomEvent("dismiss"));
  }

  private handleConnect(): void {
    this.dispatchEvent(
      new CustomEvent("connect-github", {
        detail: {
          state: this.status?.state,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** Reset dismissed state (call to force show banner again) */
  public resetDismissed(): void {
    this.dismissed = false;
    localStorage.removeItem(DISMISS_STORAGE_KEY);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-github-warning-banner": RaxGitHubWarningBanner;
  }
}
